import axios, { AxiosError } from "axios";
import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { decryptData, encryptData } from "@/plugins/crypto";
import type { AuthData } from "@/types/auth.context";
import { axiosAuth } from "./auth.client";

const root =
  import.meta.env.VITE_CONCILIATION_API_URL?.replace(/\/$/, "") ||
  "http://localhost:4005";

/** Sin Content-Type por defecto: con JSON, axios serializa FormData como objeto. */
export const axiosConciliation: AxiosInstance = axios.create({
  baseURL: `${root}/api`,
});

axiosConciliation.interceptors.request.use(
  async (
    request: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const stored = localStorage.getItem("auth");
    if (!stored) return request;

    const auth: AuthData = decryptData(stored);
    if (!auth?.access_token) return request;

    const decoded: { exp?: number } = jwtDecode(auth.access_token);
    const isExpired = dayjs.unix(decoded.exp ?? 0).diff(dayjs()) < 1;

    if (!isExpired) {
      request.headers.set("Authorization", `Bearer ${auth.access_token}`);
      return request;
    }

    try {
      const { data } = await axiosAuth.post(`/auth/refresh`, null, {
        headers: {
          Authorization: `Bearer ${auth.access_token}`,
        },
      });

      const newAuth: AuthData = { ...auth, access_token: data.access_token };
      localStorage.setItem("auth", encryptData(newAuth));
      request.headers.set("Authorization", `Bearer ${data.access_token}`);
    } catch {
      localStorage.removeItem("auth");
      window.dispatchEvent(new Event("auth:expired"));
    }

    return request;
  },
  (error) => Promise.reject(error),
);

axiosConciliation.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => Promise.reject(error.response ?? error),
);
